import {SIZE} from '../../catalog-constants';
import { PhpArray, PhpFloat, intval, phpCopy, strval, array_column } from '../../../../php-compat';
import type { PhpRef, PortEnv } from '../../../../port-contract';
import { phpAdd } from '../../../../compile/php-integer-arithmetic';
import { array, get, isArray, list, put, record, remove } from '../../array-path';
import { ftComputeSourceRow, ftComputeCollectHostSource, ftComputeCollectCarrierSources, type ComputeBaseDeps } from './_base';

export interface ReachDeps extends Pick<ComputeBaseDeps,
    '_ft_default_provider_carriers' | 'return_int_score_item_layer_type_name' | 'layer_value'> {
    ft_shapeshift_form_arr_for_speeds(host: unknown): unknown;
    item_arr_uuid_slot(value: unknown): string;
    ft_compute_collect_dynamic_integer_sources(host: unknown, layer: string, own_layer_only: boolean,
        include_feats: boolean, form: unknown): unknown;
    ft_cap_inventory_equipped_source_rows(sources: unknown): unknown;
}

/** PHP: compute_reach (includes/functions/janus/core/compute/reach.php:22) */
export function computeReach(item_arr: PhpRef<unknown>, env: PortEnv<ReachDeps>): null {
    const host = phpCopy(array(item_arr.value));
    let sources = new PhpArray();
    const form = env.deps.ft_shapeshift_form_arr_for_speeds(host), base = form ?? host;
    const size_id = env.deps.item_arr_uuid_slot(env.deps.layer_value(host, 'size') ?? '');
    let size_reach: ReturnType<typeof intval> = 0;
    if (size_id !== '') {
        const row = array_column(array(SIZE), null, 'item_id').get(size_id) ?? null;
        const raw = isArray(row) ? strval(get(row, 'reach') ?? '') : '';
        if (raw !== '') size_reach = intval(env.deps.return_int_score_item_layer_type_name(
            record({ item_layer_types: record({ reach: list(raw) }) }), 'reach', 0));
    }
    if (size_reach !== 0) sources.append(ftComputeSourceRow('size', 'Size', size_reach, size_id, 'size'));
    const host_row = ftComputeCollectHostSource(base, 'reach', env);
    if (host_row !== null) sources.append(host_row);
    for (const carrier of ftComputeCollectCarrierSources(base, 'reach', env).values()) sources.append(phpCopy(carrier));
    // Named optional arguments preserve PHP's intervening default (true).
    const dynamic = env.deps.ft_compute_collect_dynamic_integer_sources(host, 'reach', true, true, form);
    for (const row of array(get(dynamic, 0)).values()) sources.append(phpCopy(row));
    sources = array(get(env.deps.ft_cap_inventory_equipped_source_rows(sources), 0));
    let total: bigint | number = 0n;
    for (const row of sources.values()) total = phpAdd(total, BigInt(intval(get(row, 'value') ?? 0)));
    if (total === 0n || total === 0) remove(host, 'computed', 'creature', 'reach');
    else put(host, ['computed', 'creature', 'reach'], record({ total: typeof total === 'bigint' ? total : new PhpFloat(total), base: size_reach, sources }));
    item_arr.value = host;
    return null;
}
